class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    // Seule la derniere requete peut s'afficher : une reponse plus lente,
    // partie avant, ecraserait la grille avec des resultats perimes.
    if (FacetFiltersForm.abortController) FacetFiltersForm.abortController.abort();
    FacetFiltersForm.abortController = new AbortController();
    // Ce qui part maintenant fait foi : les curseurs de prix peuvent de
    // nouveau etre redessines par la reponse.
    document.querySelectorAll('price-range').forEach((priceRange) => {
      priceRange.hasPendingChange = false;
    });
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url, { signal: FacetFiltersForm.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') console.error(error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter, #FacetSortForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter, #FacetSortForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        // Un curseur de prix manipule depuis le depart de cette requete garde
        // la main : le redessiner l'arracherait au client en plein geste, et
        // sa propre requete, deja en attente, suivra.
        if (FacetFiltersForm.hasPendingPriceChange(currentElement)) return;
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        // Rendre le focus au resume arracherait le curseur des doigts (ou du
        // clavier) du client en pleine manipulation : celui qu'il vient de
        // regler, comme celui qu'il regle pendant que cette reponse arrive.
        const type = event.target.getAttribute('type');
        const keepsFocus =
          type === 'text' || type === 'range' || type === 'number' || FacetFiltersForm.hasPendingPriceChange(document);

        if (newElementToActivate && !keepsFocus) newElementToActivate.focus();
      }
    }
  }

  static hasPendingPriceChange(element) {
    return Array.from(element.querySelectorAll('price-range')).some((priceRange) => priceRange.hasPendingChange);
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const forms = [this.createSearchParams(event.target.closest('form'))];
      const sortForm = document.getElementById('FacetSortForm');
      if (sortForm) forms.push(this.createSearchParams(sortForm));
      this.onSubmitForm(forms.join('&'), event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile' || form.id === 'FacetSortForm') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.abortController = null;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

/**
 * Double curseur de prix.
 *
 * Les deux <input type="range"> pilotent deux champs caches qui portent les
 * noms attendus par Shopify. Une poignee posee sur sa butee vide son champ :
 * une plage complete ne doit pas compter comme un filtre actif.
 *
 * Les ecouteurs sont delegues sur l'element hote : renderFilters() remplace
 * le contenu des filtres par innerHTML, et chaque <price-range> ainsi recree
 * repasse par connectedCallback().
 */
class PriceRange extends HTMLElement {
  constructor() {
    super();
    // Vrai entre un geste du client et l'envoi de la requete qui le porte :
    // renderFilters() ne redessine pas ce curseur pendant ce temps.
    this.hasPendingChange = false;
  }

  connectedCallback() {
    if (!this.listenersAttached) {
      this.listenersAttached = true;
      this.addEventListener('input', this.onHandleInput.bind(this));
      this.addEventListener('pointermove', this.onPointerMove.bind(this));
    }
    this.sync();
  }

  get root() {
    return this.querySelector('[data-price-range]');
  }

  get minHandle() {
    return this.querySelector('[data-price-handle="min"]');
  }

  get maxHandle() {
    return this.querySelector('[data-price-handle="max"]');
  }

  get rangeMax() {
    const root = this.root;
    const value = root ? Number(root.dataset.max) : NaN;
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  onHandleInput(event) {
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    if (!target.closest('[data-price-handle]')) return;

    this.hasPendingChange = true;
    this.clamp(target.dataset.priceHandle);
    this.sync();
  }

  /**
   * Deux <input type="range"> superposes : quand les pouces se rejoignent,
   * seul celui du dessus reste attrapable. A la souris, on remonte celui dont
   * le pointeur s'approche, avant que le clic ne choisisse.
   */
  onPointerMove(event) {
    if (event.pointerType !== 'mouse' || event.buttons) return;

    const slider = this.querySelector('.price-range__slider');
    const minHandle = this.minHandle;
    const maxHandle = this.maxHandle;
    if (!slider || !minHandle || !maxHandle) return;

    const bounds = slider.getBoundingClientRect();
    const thumb = minHandle.offsetHeight;
    const course = bounds.width - thumb;
    if (course <= 0) return;

    const value = ((event.clientX - bounds.left - thumb / 2) / course) * this.rangeMax;
    const closerToMax = Math.abs(value - Number(maxHandle.value)) < Math.abs(value - Number(minHandle.value));
    this.stack(closerToMax ? 'max' : 'min');
  }

  /** Passe la poignee nommee au-dessus de l'autre. */
  stack(onTop) {
    const minHandle = this.minHandle;
    const maxHandle = this.maxHandle;
    if (!minHandle || !maxHandle) return;

    minHandle.style.zIndex = onTop === 'min' ? '3' : '1';
    maxHandle.style.zIndex = onTop === 'min' ? '1' : '3';
  }

  /**
   * Empeche les poignees de se croiser, et de se superposer exactement : deux
   * pouces au meme pixel, l'un recouvre l'autre et devient inattrapable.
   */
  clamp(moved) {
    const minHandle = this.minHandle;
    const maxHandle = this.maxHandle;
    if (!minHandle || !maxHandle) return;

    const min = Number(minHandle.value);
    const max = Number(maxHandle.value);
    if (min < max) return;

    if (moved === 'max') {
      maxHandle.value = String(Math.min(this.rangeMax, min + 1));
    } else {
      minHandle.value = String(Math.max(0, max - 1));
    }
  }

  /** Reporte l'etat des poignees vers les champs soumis, les montants et la barre. */
  sync() {
    const root = this.root;
    const minHandle = this.minHandle;
    const maxHandle = this.maxHandle;
    if (!root || !minHandle || !maxHandle) return;

    const rangeMax = this.rangeMax;
    const min = Number(minHandle.value);
    const max = Number(maxHandle.value);

    const minField = this.querySelector('[data-price-field="min"]');
    const maxField = this.querySelector('[data-price-field="max"]');
    if (minField) minField.value = min > 0 ? String(min) : '';
    if (maxField) maxField.value = max < rangeMax ? String(max) : '';

    const minLabel = this.formatMoney(min, root);
    const maxLabel = this.formatMoney(max, root);
    this.setText(this.querySelector('[data-price-output="min"]'), minLabel);
    this.setText(this.querySelector('[data-price-output="max"]'), maxLabel);

    root.style.setProperty('--price-min-ratio', String(min / rangeMax));
    root.style.setProperty('--price-max-ratio', String(max / rangeMax));

    minHandle.setAttribute('aria-valuetext', minLabel);
    maxHandle.setAttribute('aria-valuetext', maxLabel);

    // Sans souris pour departager (tactile, clavier), des pouces qui se
    // rejoignent en haut de la plage ne peuvent que redescendre : le minimum
    // passe dessus. En bas de la plage, c'est l'inverse.
    this.stack((min + max) / 2 > rangeMax / 2 ? 'min' : 'max');
  }

  /**
   * Modifie le noeud texte en place plutot que de le remplacer : le bloc de
   * filtres observe les ajouts et retraits de noeuds du panneau mobile, et
   * rouvrirait tous les accordeons a chaque pas du curseur.
   */
  setText(element, text) {
    if (!element) return;

    const node = element.firstChild;
    if (node && node.nodeType === Node.TEXT_NODE && !node.nextSibling) {
      if (node.data !== text) node.data = text;
    } else {
      element.textContent = text;
    }
  }

  formatMoney(value, root) {
    const currency = root.dataset.currency;
    const locale = root.dataset.locale || document.documentElement.lang || undefined;

    if (currency) {
      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value);
      } catch (error) {
        // Locale ou devise refusee : on retombe sur le symbole de la boutique.
      }
    }

    return `${value}${root.dataset.currencySymbol ? ` ${root.dataset.currencySymbol}` : ''}`;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
